// connectionHighlight — pure branch-activation matching for edge visibility.
//
// Migrated off the legacy `activatedBranches` route-string vs `conn.style`
// comparison: new-format topologies carry per-branch-point activation records
// (`{frameId, condition, route}`, condition→condition route contract), so an
// edge lights only when a record at ITS OWN frame matches its declared
// condition. The legacy style-based match survives ONLY for old-format
// connections (no `eventType` — the same old-format marker the assertion
// engine uses), whose predicate routes are style names like 'happy'/'terminal'.

import type { ConnectionDef, ConnectionStyleDef } from '../../types/index.ts';
import type { BranchActivationRecord } from '../../store/slices/simulationSlice.ts';
import { isConnectionAlwaysVisible } from '../../services/styleResolver.ts';

/**
 * Is this connection activated by one of the run's branch decisions?
 *
 * New-format connections (they carry `eventType`) match per branch point:
 * a record must be scoped to the edge's own frame (`conn.from === frameId`)
 * AND identify the edge's arm — via `conn.branchCondition` when the topology
 * declares it, else via `conn.style` compared against the record's condition
 * and mapped route (condition→condition contract makes these the condition
 * strings). A declared-but-different condition NEVER matches, even if the
 * style string would.
 *
 * Legacy fallback (connections without `eventType`, i.e. old-format
 * topologies): the pre-migration behavior — activated route strings matched
 * against `conn.style` — because old predicate routes ARE style names.
 */
export function isBranchActivatedConnection(
  conn: ConnectionDef,
  records: BranchActivationRecord[],
  legacyActivatedRoutes: string[],
): boolean {
  for (const record of records) {
    if (conn.from !== record.frameId) continue;
    if (typeof conn.branchCondition === 'string') {
      if (conn.branchCondition === record.condition || conn.branchCondition === record.route) {
        return true;
      }
      continue; // edge declares a DIFFERENT arm — do not light it
    }
    if (conn.style === record.condition || conn.style === record.route) return true;
  }

  if (typeof conn.eventType !== 'string') {
    // Old-format topology — style-based route matching is the only signal.
    return legacyActivatedRoutes.includes(conn.style);
  }
  return false;
}

/** Full visibility rule for an edge (always-visible style, active path, or branch activation). */
export function isConnectionVisible(
  conn: ConnectionDef,
  activePaths: string[],
  records: BranchActivationRecord[],
  legacyActivatedRoutes: string[],
  topologyStyles?: Record<string, ConnectionStyleDef>,
): boolean {
  if (isConnectionAlwaysVisible(conn.style, topologyStyles)) return true;
  if (activePaths.includes(conn.style)) return true;
  return isBranchActivatedConnection(conn, records, legacyActivatedRoutes);
}
